import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ANALYTICS_SOURCE, OPEN_GRAPH_ACTION_SUBJECT_ID } from '../analytics.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';

// Button has fixed vertical padding, so the footer uses Pressable to match the design.
const styles = cssMap({
	footerButton: {
		display: 'flex',
		width: '100%',
		height: '32px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'stretch',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		font: token('font.body'),
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

const i18n = defineMessages({
	label: {
		id: 'byline-connections.preview-panel.open-graph-button.view-teamwork-graph',
		defaultMessage: 'View Teamwork Graph',
		description:
			'Label of the button in the Teamwork Graph preview panel footer that opens the full-size Teamwork Graph modal. "Teamwork Graph" is a product name.',
	},
});

export type TeamworkGraphPreviewPanelOpenGraphButtonProps = {
	onClick: () => void;
	testId?: string;
};

export const TeamworkGraphPreviewPanelOpenGraphButton = ({
	onClick,
	testId = 'teamwork-graph-preview-panel.open-graph-button',
}: TeamworkGraphPreviewPanelOpenGraphButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: OPEN_GRAPH_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
		onClick();
	}, [createAnalyticsEvent, onClick]);

	return (
		<Pressable xcss={styles.footerButton} onClick={handleClick} testId={testId}>
			<Text color="color.text.subtle" size="medium" weight="medium" maxLines={1}>
				{formatMessage(i18n.label)}
			</Text>
		</Pressable>
	);
};
